import { useCallback, useMemo, useState } from 'react';
import { IconFileText, IconPhoto } from '@tabler/icons-react';
import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  Metric,
  PaginatedTable,
  Panel,
  SegmentedControl,
  formatDate,
  formatNumber,
  useToast,
  type DataSource,
  type FilterSpec,
  type RemoteOption,
  type RemoteSearch,
  type Row,
  type RawCellValue,
  type TableColumn,
  type TableResult,
  type TableSelectSource,
  type TableQuery,
} from 'escrowbuild-ui';

import {
  useDocuments,
  useEvidence,
  useFixtureVocabulary,
  useT,
  type AppTranslate,
  type AppTranslationKey,
} from '@/i18n';
import {
  DOCUMENTS,
  EVIDENCE,
  PHASE_SEQUENCE,
  PROJECTS,
  type DocumentRecord,
  type EvidenceItem,
} from '@/data/domain';

type PublicationFilter = 'all' | 'published' | 'unpublished';

const PROJECT_NAME = new Map(PROJECTS.map((project) => [project.id, project.name]));

/** Only the phases the fixtures actually use — a filter that can only ever return nothing is a trap. */
const PHASE_OPTIONS = PHASE_SEQUENCE.filter(
  (phase) =>
    EVIDENCE.some((item) => item.phaseId === phase.id) ||
    DOCUMENTS.some((document) => document.phaseId === phase.id),
);

/** Same rule for the kinds: the demo has photos, and offering "video" would only ever empty the grid. */
const KIND_OPTIONS = [...new Set(EVIDENCE.map((item) => item.kind))];

/**
 * The gallery is capped. Fifty cards would push the documents table — the half
 * of this screen that carries the two-flag rule — nine screens down the page.
 */
const GALLERY_LIMIT = 12;

const DOCUMENT_TYPES = [...new Set(DOCUMENTS.map((document) => document.type))].sort();
const VERIFICATION_STATES = ['verified', 'in-review', 'rejected'] as const;

const TYPE_LABEL: Record<DocumentRecord['type'], AppTranslationKey> = {
  invoice: 'status.documentType.invoice',
  receipt: 'status.documentType.receipt',
  contract: 'status.documentType.contract',
  permit: 'status.documentType.permit',
  quote: 'status.documentType.quote',
  report: 'status.documentType.report',
  inspection: 'status.documentType.inspection',
};

const KIND_LABEL: Record<EvidenceItem['kind'], AppTranslationKey> = {
  photo: 'status.evidenceKind.photo',
  video: 'status.evidenceKind.video',
  note: 'status.evidenceKind.note',
};

const VERIFICATION_LABEL: Record<(typeof VERIFICATION_STATES)[number], AppTranslationKey> = {
  verified: 'status.verification.verified',
  'in-review': 'status.verification.inReview',
  rejected: 'status.verification.rejected',
};

const VERIFICATION_TONE = {
  verified: 'success',
  'in-review': 'info',
  rejected: 'error',
} as const;

/* -----------------------------------------------------------------------------
   Table plumbing
   -------------------------------------------------------------------------- */

/** The row the documents table sorts, filters and paginates over. */
interface DocumentRow extends Row {
  id: string;
  document: string;
  project: string;
  phase: string;
  type: string;
  verification: string;
  size: string;
  date: string;
  uploadedBy: string;
}

/**
 * The documents table's columns.
 *
 * Built from `t` rather than declared at module scope: a header is interface
 * text like any other, and a constant could only ever carry one language.
 */
/** A document's kind, from the code the row holds. */
function documentColumns(t: AppTranslate): TableColumn[] {
  const asDocumentType = (value: RawCellValue): string =>
    typeof value === 'string' && TYPE_LABEL[value as DocumentRecord['type']]
      ? t(TYPE_LABEL[value as DocumentRecord['type']])
      : String(value ?? '');

  return [
    {
      key: 'document',
      header: t('evidence.col.document'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      width: '28%',
      aggregate: { op: 'count' },
    },
    {
      key: 'project',
      header: t('evidence.col.project'),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    { key: 'phase', header: t('evidence.col.phase'), sortable: true, filterable: true, filterType: 'text' },
    { key: 'type', header: t('evidence.col.type'), sortable: true, filterable: true, filterType: 'select', formatter: asDocumentType },
    {
      key: 'verification',
      header: t('evidence.col.verification'),
      sortable: true,
      filterable: true,
      filterType: 'select',
      // The first of the two flags. It is verified or it is not, and that says
      // nothing yet about whether an investor has seen it — the Release column
      // below is deliberately a second, independent answer.
      formatter: (value) => {
        const state = String(value ?? '') as keyof typeof VERIFICATION_LABEL;
        return VERIFICATION_LABEL[state] ? t(VERIFICATION_LABEL[state]) : String(value ?? '');
      },
    },
    {
      key: 'release',
      header: t('evidence.col.release'),
      sortable: true,
      formatter: (value) =>
        value === 'published' ? t('status.publication.published') : t('evidence.notReleased'),
    },
    {
      key: 'date',
      header: t('common.date'),
      sortable: true,
      filterable: true,
      filterType: 'date',
      formatter: (value) => formatDate(String(value ?? '')),
    },
    { key: 'size', header: t('evidence.col.size'), align: 'right', hiddenByDefault: true },
    {
      key: 'uploadedBy',
      header: t('evidence.col.uploadedBy'),
      sortable: true,
      filterable: true,
      filterType: 'text',
      hiddenByDefault: true,
    },
  ];
}

function documentSelectSources(t: AppTranslate): Record<string, TableSelectSource> {
  return {
    type: {
      search: staticSearch(DOCUMENT_TYPES.map((type) => ({ value: type, label: t(TYPE_LABEL[type]) }))),
      minSearchLength: 0,
    },
    verification: {
      search: staticSearch(
        VERIFICATION_STATES.map((state) => ({ value: state, label: t(VERIFICATION_LABEL[state]) })),
      ),
      minSearchLength: 0,
    },
  };
}

/**
 * A `RemoteSearch` over a list the page already holds.
 *
 * The table's select filter is written against the API contract, so it is given
 * one even though these options come from the fixture: the shape stays honest, an
 * aborted request is refused rather than answered late, and swapping in a real
 * endpoint later is a change to this one function.
 */
function staticSearch(options: RemoteOption[]): RemoteSearch {
  return async (term, signal) => {
    await Promise.resolve();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const needle = term.trim().toLowerCase();
    const matched = options.filter((option) => option.label.toLowerCase().includes(needle));
    return { options: matched.slice(0, 20), truncated: matched.length > 20 };
  };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

function matches(row: DocumentRow, filter: FilterSpec): boolean {
  const cell = row[filter.field];

  switch (filter.op) {
    case 'contains':
      return String(cell ?? '').toLowerCase().includes(String(filter.value ?? '').toLowerCase());
    case 'in':
      return Array.isArray(filter.value)
        ? filter.value.length === 0 || filter.value.some((member) => String(member) === String(cell))
        : true;
    case 'eq':
      return String(cell ?? '') === String(filter.value ?? '');
    case 'ne':
      return String(cell ?? '') !== String(filter.value ?? '');
    case 'isNull':
      return cell === null || cell === undefined || cell === '';
    case 'isNotNull':
      return cell !== null && cell !== undefined && cell !== '';
    case 'between': {
      if (!Array.isArray(filter.value) || filter.value.length !== 2) return true;
      const [lo, hi] = filter.value as [unknown, unknown];
      const text = String(cell ?? '');
      // A date filter emits the closed day as `<day>T00:00:00`–`<day>T23:59:59`,
      // so the cell is compared on its own day part rather than as a string.
      if (ISO_DAY.test(text)) {
        const day = text.slice(0, 10);
        const from = lo ? String(lo).slice(0, 10) : '';
        const to = hi ? String(hi).slice(0, 10) : '';
        return (from === '' || day >= from) && (to === '' || day <= to);
      }
      const value = Number(text);
      return (
        (lo === undefined || lo === null || lo === '' || value >= Number(lo)) &&
        (hi === undefined || hi === null || hi === '' || value <= Number(hi))
      );
    }
    default:
      return true;
  }
}

function runQuery(rows: DocumentRow[], query: TableQuery): TableResult<DocumentRow> {
  // One filter per field: a later spec replaces the earlier one rather than compounding.
  const effective = new Map<string, FilterSpec>();
  for (const filter of query.filters) effective.set(filter.field, filter);

  const filtered = rows.filter((row) => [...effective.values()].every((filter) => matches(row, filter)));

  const sort = query.sort;
  const sorted = sort
    ? [...filtered].sort((a, b) => {
        const av = a[sort.field];
        const bv = b[sort.field];
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const result =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), 'en', { numeric: true, sensitivity: 'base' });
        return sort.dir === 'asc' ? result : -result;
      })
    : filtered;

  const aggregations: Record<string, number | null> = {};
  for (const spec of query.aggregations) {
    if (spec.op === 'count') aggregations[spec.field] = filtered.length;
    else if (spec.op === 'sum' || spec.op === 'avg') {
      const numbers = filtered
        .map((row) => Number(row[spec.field]))
        .filter((value) => Number.isFinite(value));
      aggregations[spec.field] = numbers.length === 0
        ? null
        : spec.op === 'sum'
          ? numbers.reduce((total, value) => total + value, 0)
          : numbers.reduce((total, value) => total + value, 0) / numbers.length;
    } else aggregations[spec.field] = null;
  }

  const total = sorted.length;
  const lastPage = Math.max(1, Math.ceil(total / query.pageSize));
  const page = Math.min(Math.max(query.page, 1), lastPage);
  const start = (page - 1) * query.pageSize;

  return {
    rows: sorted.slice(start, start + query.pageSize),
    total,
    page,
    pageSize: query.pageSize,
    aggregations,
  };
}

/* -----------------------------------------------------------------------------
   The page
   -------------------------------------------------------------------------- */

export function EvidencePage() {
  const t = useT();
  const { toast } = useToast();

  // Built once per locale: the table reads its column set and its filter
  // sources as identity-stable objects, and rebuilding either per render would
  // hand it a new one on every keystroke in the filters above.
  const columns = useMemo(() => documentColumns(t), [t]);
  const selectSources = useMemo(() => documentSelectSources(t), [t]);

  /*
     The library and the filing cabinet, resolved for the active locale: a
     caption and a document's name are the records' own words. The counts below
     are the same either way — the lists a language resolves are the same rows.
  */
  const allEvidence = useEvidence();
  const allDocuments = useDocuments();
  const vocabulary = useFixtureVocabulary();

  const [projectId, setProjectId] = useState('all');
  const [phaseId, setPhaseId] = useState('all');
  const [publication, setPublication] = useState<PublicationFilter>('all');
  const [kind, setKind] = useState('all');
  /** The demo's own edits to the publication flag, over the frozen fixture. */
  const [released, setReleased] = useState<Record<string, boolean>>({});
  const [showAllEvidence, setShowAllEvidence] = useState(false);

  const isPublished = useCallback(
    (item: EvidenceItem) => released[item.id] ?? item.published,
    [released],
  );

  const evidence = useMemo(
    () =>
      allEvidence.filter((item) => {
        if (projectId !== 'all' && item.projectId !== projectId) return false;
        if (phaseId !== 'all' && item.phaseId !== phaseId) return false;
        if (kind !== 'all' && item.kind !== kind) return false;
        if (publication === 'published' && !isPublished(item)) return false;
        if (publication === 'unpublished' && isPublished(item)) return false;
        return true;
      }),
    [projectId, phaseId, kind, publication, isPublished],
  );

  const documents = useMemo(
    () =>
      allDocuments.filter((document) => {
        if (projectId !== 'all' && document.projectId !== projectId) return false;
        if (phaseId !== 'all' && document.phaseId !== phaseId) return false;
        if (publication === 'published' && !document.published) return false;
        if (publication === 'unpublished' && document.published) return false;
        return true;
      }),
    [projectId, phaseId, publication],
  );

  const documentSource: DataSource<DocumentRow> = useCallback(
    async (query, signal) => {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const rows: DocumentRow[] = documents.map((document) => ({
        id: document.id,
        document: document.name,
        project: PROJECT_NAME.get(document.projectId) ?? document.projectId,
        phase: document.phaseId
          ? vocabulary.phase(document.phaseId)
          : t('evidence.noPhase'),
        type: document.type,
        // The two flags, carried as two separate columns: verification is the
        // document's own state, release is whether the portal has it.
        verification: document.status,
        release: document.published ? 'published' : 'unreleased',
        size: document.size,
        date: document.date,
        uploadedBy: document.uploadedBy,
      }));
      return runQuery(rows, query);
    },
    [documents, t],
  );

  const publishedEvidence = allEvidence.filter((item) => isPublished(item)).length;
  const unpublishedEvidence = allEvidence.length - publishedEvidence;
  const verifiedUnreleased = allDocuments.filter(
    (document) => document.status === 'verified' && !document.published,
  );
  const unreleasedDocuments = allDocuments.filter((document) => !document.published);
  const awaitingPublication = unpublishedEvidence + unreleasedDocuments.length;
  const withoutPhase = allDocuments.filter((document) => document.phaseId === null);

  const visibleEvidence = showAllEvidence ? evidence : evidence.slice(0, GALLERY_LIMIT);
  const filtersActive = projectId !== 'all' || phaseId !== 'all' || publication !== 'all' || kind !== 'all';

  const resetFilters = () => {
    setProjectId('all');
    setPhaseId('all');
    setPublication('all');
    setKind('all');
  };

  const togglePublication = (item: EvidenceItem) => {
    const next = !isPublished(item);
    setReleased((current) => ({ ...current, [item.id]: next }));
    toast({
      variant: next ? 'success' : 'warning',
      title: next ? t('evidence.toast.released') : t('evidence.toast.withdrawn'),
      description: t('evidence.toast.description', {
        caption: item.caption,
        phase: vocabulary.phase(item.phaseId)
      }),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('evidence.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('evidence.subtitle')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('evidence.metric.items')}
          value={allEvidence.length}
          format="number"
          hint={t('evidence.metric.itemsHint', { released: publishedEvidence })}
        />
        <Metric
          label={t('evidence.metric.documents')}
          value={allDocuments.length}
          format="number"
          hint={t('evidence.metric.documentsHint', {
            released: allDocuments.length - unreleasedDocuments.length,
            projects: PROJECTS.length,
          })}
        />
        <Metric
          label={t('evidence.metric.awaiting')}
          value={awaitingPublication}
          format="number"
          tone={awaitingPublication > 0 ? 'warning' : 'success'}
          hint={t('evidence.metric.awaitingHint')}
        />
        <Metric
          label={t('evidence.metric.verifiedUnreleased')}
          value={verifiedUnreleased.length}
          format="number"
          tone={verifiedUnreleased.length > 0 ? 'warning' : 'success'}
          hint={t('evidence.metric.verifiedUnreleasedHint')}
        />
      </div>

      <Panel
        title={t('evidence.gate.title')}
        description={t('evidence.gate.description')}
        headerActions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPublication(publication === 'unpublished' ? 'all' : 'unpublished')}
          >
            {publication === 'unpublished'
              ? t('evidence.gate.showEverything')
              : t('evidence.gate.showOnlyUnpublished')}
          </Button>
        }
        padding="lg"
      >
        <div className="flex flex-col gap-4">
          <InlineAlert
            tone="warning"
            title={t('evidence.gate.alert.title', {
              awaiting: awaitingPublication,
              total: allEvidence.length + allDocuments.length,
            })}
            description={t('evidence.gate.alert.description', {
              documents: unreleasedDocuments.length,
              evidence: unpublishedEvidence,
              verified: verifiedUnreleased.length,
            })}
          />

          {/*
            §14 requires a photo or an expense to be attached to the right project
            AND phase before publication. The check runs here so the client can
            see it happen rather than take it on trust.
          */}
          {withoutPhase.length === 0 ? (
            <InlineAlert
              tone="success"
              title={t('evidence.gate.phase.title')}
              description={t('evidence.gate.phase.description', { documents: allDocuments.length })}
            />
          ) : (
            <InlineAlert
              tone="warning"
              title={t('evidence.gate.noPhase.title', { count: withoutPhase.length })}
              description={t('evidence.gate.noPhase.description', {
                names: withoutPhase.map((document) => document.name).join(' · '),
              })}
            />
          )}

          <div>
            <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('evidence.gate.heldBack')}
            </h3>
            <ul className="mt-2 flex list-none flex-col divide-y divide-line-subtle p-0">
              {[...unreleasedDocuments]
                .sort((a, b) => b.date.localeCompare(a.date))
                .slice(0, 5)
                .map((document) => (
                  <li key={document.id} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-heading">{document.name}</span>
                      <span className="block text-xs text-muted">
                        {PROJECT_NAME.get(document.projectId)} · {t(TYPE_LABEL[document.type])} ·{' '}
                        {formatDate(document.date, { style: 'medium' })}
                      </span>
                    </span>
                    <Badge tone={VERIFICATION_TONE[document.status]} variant="subtle" size="sm">
                      {t(VERIFICATION_LABEL[document.status])}
                    </Badge>
                    <Badge tone="warning" variant="subtle" size="sm">
                      {t('evidence.notReleased')}
                    </Badge>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </Panel>

      <Panel
        title={t('evidence.filters.title')}
        description={t('evidence.filters.description')}
        padding="lg"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <SegmentedControl
              label={t('evidence.filter.project')}
              size="sm"
              value={projectId}
              onValueChange={setProjectId}
              options={[
                { value: 'all', label: t('evidence.filter.allProjects') },
                ...PROJECTS.map((project) => ({
                  value: project.id,
                  label: project.name.replace(' Residence', ''),
                })),
              ]}
            />
            <SegmentedControl
              label={t('evidence.filter.publication')}
              size="sm"
              value={publication}
              onValueChange={(value) => setPublication(value as PublicationFilter)}
              options={[
                { value: 'all', label: t('evidence.filter.anyState') },
                { value: 'published', label: t('status.publication.published') },
                { value: 'unpublished', label: t('evidence.notPublished') },
              ]}
            />
          </div>

          <div role="group" aria-label={t('evidence.filter.phase')} className="flex flex-col gap-2">
            <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('evidence.filter.phase')}
            </span>
            <div className="flex flex-wrap gap-2">
              <Badge
                selectable
                selected={phaseId === 'all'}
                onSelectedChange={() => setPhaseId('all')}
              >
                {t('evidence.filter.allPhases')}
              </Badge>
              {PHASE_OPTIONS.map((phase) => (
                <Badge
                  key={phase.id}
                  selectable
                  selected={phaseId === phase.id}
                  onSelectedChange={() => setPhaseId(phase.id)}
                >
                  {vocabulary.phase(phase.id)}
                </Badge>
              ))}
            </div>
          </div>

          {KIND_OPTIONS.length > 1 ? (
            <div role="group" aria-label={t('evidence.filter.kind')} className="flex flex-col gap-2">
              <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('evidence.filter.kind')}
              </span>
              <div className="flex flex-wrap gap-2">
                <Badge selectable selected={kind === 'all'} onSelectedChange={() => setKind('all')}>
                  {t('evidence.filter.allKinds')}
                </Badge>
                {KIND_OPTIONS.map((entry) => (
                  <Badge
                    key={entry}
                    selectable
                    selected={kind === entry}
                    onSelectedChange={() => setKind(entry)}
                  >
                    {t(KIND_LABEL[entry])}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-subtle pt-3">
            <p role="status" className="text-xs text-body">
              {t('evidence.showing', {
                evidence: formatNumber(evidence.length),
                totalEvidence: formatNumber(allEvidence.length),
                documents: formatNumber(documents.length),
                totalDocuments: formatNumber(allDocuments.length),
              })}
            </p>
            <Button variant="ghost" size="sm" disabled={!filtersActive} onClick={resetFilters}>
              {t('evidence.clearFilters')}
            </Button>
          </div>
        </div>
      </Panel>

      <Panel
        title={
          <span className="flex items-center gap-2">
            <IconPhoto size={16} aria-hidden="true" />
            {t('evidence.gallery.title')}
          </span>
        }
        description={t('evidence.gallery.description', {
          shown: formatNumber(evidence.length),
          total: formatNumber(allEvidence.length),
        })}
        padding="lg"
      >
        {evidence.length === 0 ? (
          <EmptyState
            variant="cards"
            icon={<IconPhoto size={28} aria-hidden="true" />}
            title={t('evidence.gallery.empty.title')}
            description={t('evidence.gallery.empty.description')}
            action={
              <Button variant="outline" size="sm" onClick={resetFilters}>
                {t('evidence.clearFilters')}
              </Button>
            }
          />
        ) : (
          <ul className="grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleEvidence.map((item) => {
              const published = isPublished(item);
              const phaseName = vocabulary.phase(item.phaseId)
              return (
                <li
                  key={item.id}
                  className="flex flex-col overflow-hidden rounded-xs border border-line bg-surface"
                >
                  {/* No image file exists for these records, so the frame stands in
                      for the capture rather than loading a placeholder from a CDN. */}
                  <div className="relative grid aspect-[4/3] place-items-center bg-surface-muted">
                    <span aria-hidden="true" className="font-display text-4xl font-semibold text-line-strong">
                      {phaseInitials(phaseName)}
                    </span>
                    <span className="absolute left-2 top-2">
                      <Badge tone="neutral" variant="solid" size="sm">
                        {t(KIND_LABEL[item.kind])}
                      </Badge>
                    </span>
                    <span className="absolute right-2 top-2">
                      <Badge tone={published ? 'success' : 'warning'} variant="solid" size="sm">
                        {published ? t('status.publication.published') : t('evidence.notPublished')}
                      </Badge>
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col gap-1 p-3">
                    <p className="text-sm font-medium text-heading">{item.caption}</p>
                    <p className="text-xs text-muted">
                      {PROJECT_NAME.get(item.projectId)} · {phaseName}
                    </p>
                    <p className="text-xs text-muted">
                      {formatDate(item.date, { style: 'medium' })} ·{' '}
                      {t('evidence.capturedBy', { name: item.capturedBy })}
                    </p>
                    <div className="mt-auto flex items-center justify-end pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={
                          published
                            ? t('evidence.withdrawAria', { caption: item.caption })
                            : t('evidence.publishAria', { caption: item.caption })
                        }
                        onClick={() => togglePublication(item)}
                      >
                        {published ? t('evidence.withdraw') : t('evidence.publish')}
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {evidence.length > GALLERY_LIMIT ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line-subtle pt-3">
            <p role="status" className="text-xs text-body">
              {t('evidence.gallery.showing', {
                shown: formatNumber(visibleEvidence.length),
                total: formatNumber(evidence.length),
              })}
            </p>
            <Button variant="outline" size="sm" onClick={() => setShowAllEvidence((shown) => !shown)}>
              {showAllEvidence
                ? t('evidence.gallery.showFewer')
                : t('evidence.gallery.showAll', { total: formatNumber(evidence.length) })}
            </Button>
          </div>
        ) : null}
      </Panel>

      <Panel
        title={
          <span className="flex items-center gap-2">
            <IconFileText size={16} aria-hidden="true" />
            {t('evidence.documents.title')}
          </span>
        }
        description={t('evidence.documents.description')}
        padding="none"
      >
        <div className="p-4">
          <PaginatedTable<DocumentRow>
            id="evidence-documents"
            caption={t('evidence.documents.caption')}
            columns={columns}
            dataSource={documentSource}
            rowKey="id"
            selectSources={selectSources}
            defaultPageSize={10}
            pageSizeOptions={[10, 25, 50]}
            emptyTitle={t('evidence.documents.emptyTitle')}
            emptyDescription={t('evidence.documents.emptyDescription')}
          />
        </div>
      </Panel>
    </div>
  );
}

/** Two letters from the phase name, so a card without a picture still says where it belongs. */
function phaseInitials(name: string): string {
  return name
    .split(' ')
    .filter((word) => word.length > 3)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}
